import React, { useRef } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { motion, useInView } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import GradientMeshBackground from './ui/GradientMeshBackground';
import Scene3D from './ui/Scene3D';
import MagneticButton from './ui/MagneticButton';

export default function Footer() {
  const navigate = useNavigate();
  const ctaRef = useRef(null);
  const ctaInView = useInView(ctaRef, { once: true, margin: '-80px' });

  const featuredServices = [
    { name: "Search Engine Optimization (SEO)", path: "/services/seo" },
    { name: "Social Media Marketing", path: "/services/social-media-marketing" },
    { name: "Google Ads & PPC", path: "/services/google-ads" },
    { name: "Website Designing", path: "/services/website-designing" },
    { name: "App Development", path: "/services/app-development" },
    { name: "Influencer Marketing", path: "/services/influencer-marketing" },
    { name: "WhatsApp Marketing", path: "/services/whatsapp-marketing" },
    { name: "Online Reputation Management", path: "/services/orm" },
    { name: "PR Agency", path: "/services/pr-agency" },
    { name: "Email Marketing", path: "/services/email-marketing" },
  ];

  const importantLinks = [
    { name: "About Us", path: "/aboutus" },
    { name: "Blog", path: "/blog" },
    { name: "Pricing Plans", path: "/pricing" },
    { name: "Careers", path: "/careers" },
    { name: "FAQ's", path: "/faqs" },
    { name: "Privacy Policy", path: "/privacy-policy" },
    { name: "Sitemap", path: "https://www.slgt.in/sitemap.xml", isExternal: true },
    { name: "Request a Quote", path: "/requestquotes" },
  ];

  const MotionLink = motion(Link);

  return (
    <footer role="contentinfo">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["ProfessionalService", "Organization"],
            "name": "SLGT Digital Marketing Agency",
            "url": "https://www.slgt.in",
            "logo": "https://www.slgt.in/assets/slgt-logo.png",
            "image": "https://www.slgt.in/og.png",
            "email": "slgtmarketingagency@gmail.com",
            "telephone": "+91-8500501823",
            "priceRange": "₹₹",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1st Floor, Gandhi Market, Anakapalle",
              "addressLocality": "Anakapalli",
              "addressRegion": "Andhra Pradesh",
              "postalCode": "531001",
              "addressCountry": "IN"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 17.6910,
              "longitude": 82.9948
            },
            "areaServed": [
              { "@type": "City", "name": "Anakapalli" },
              { "@type": "City", "name": "Visakhapatnam" },
              { "@type": "City", "name": "Vizag" }
            ],
            "sameAs": [
              "https://www.facebook.com/people/SLGT-Digital-Marketing-Agency/61572272860427/",
              "https://www.instagram.com/slgt_digital_marketing_agency",
              "https://www.linkedin.com/in/slgt-digital-marketing-agency-396936346/"
            ]
          })}
        </script>
      </Helmet>

      <Box
        sx={{
          background: 'linear-gradient(165deg, #0b0b12 0%, #16110d 60%, #1a1310 100%)',
          color: 'white',
          pt: { xs: 6, md: 8 },
          pb: { xs: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #ff9800, #ff5722)',
            zIndex: 2,
          }
        }}
      >
        <GradientMeshBackground
          blobs={[
            { size: 380, top: '-15%', left: '75%', color: 'rgba(253,129,18,0.28)', duration: 22 },
            { size: 300, top: '60%', left: '-8%', color: 'rgba(255,87,34,0.2)', duration: 26 },
          ]}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            ref={ctaRef}
            sx={{
              position: 'relative',
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.03)',
              p: { xs: 4, md: 6 },
              mb: { xs: 5, md: 7 },
              overflow: 'hidden',
              textAlign: 'center',
            }}
          >
            {ctaInView && (
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.55 }}>
                <Scene3D variant="footer" />
              </Box>
            )}
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  color: '#fff',
                  fontWeight: 800,
                  mb: 1.5,
                  fontSize: { xs: '1.6rem', md: '2.2rem' },
                }}
              >
                Ready to grow something{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(90deg, #FD8112, #FF5F6D)',
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    color: 'transparent',
                  }}
                >
                  extraordinary
                </Box>
                ?
              </Typography>
              <Typography sx={{ color: '#B0B0B0', mb: 3, maxWidth: 520, mx: 'auto' }}>
                Tell us about your brand and get a free, no-obligation growth strategy call with our team.
              </Typography>
              <MagneticButton
                variant="contained"
                onClick={() => navigate('/requestquotes')}
                sx={{
                  background: 'linear-gradient(135deg, #FD8112, #FF5F6D)',
                  color: '#fff',
                  py: 1.5,
                  px: 4,
                }}
              >
                Get Free Strategy Call
              </MagneticButton>
            </Box>
          </Box>

          <Grid container spacing={{ xs: 4, md: 3 }}>
            <Grid item xs={12} md={4}>
              <motion.img
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
                src="/assets/slgt-logo.png"
                alt="SLGT Digital Marketing Agency Logo"
                style={{ height: '140px', cursor: 'pointer', marginBottom: '1rem' }}
                onClick={() => navigate('/')}
              />
              <Typography variant="body2" sx={{ color: '#B0B0B0', fontSize: '0.95rem', lineHeight: 1.8, mb: 2 }}>
                SLGT is a results-driven digital marketing agency in Visakhapatnam, offering SEO, SEM, social media, content marketing, and web design to grow your business online.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <MotionLink
                  href="https://www.facebook.com/people/SLGT-Digital-Marketing-Agency/61572272860427/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  whileHover={{ y: -4, scale: 1.08 }}
                  sx={{
                    color: '#B0B0B0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(8px)',
                    '&:hover': { color: '#fff', background: 'rgba(253,129,18,0.85)', boxShadow: '0 8px 20px rgba(253,129,18,0.4)' },
                  }}
                >
                  <FacebookIcon fontSize="small" />
                </MotionLink>
                <MotionLink
                  href="https://www.linkedin.com/in/slgt-digital-marketing-agency-396936346/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  whileHover={{ y: -4, scale: 1.08 }}
                  sx={{
                    color: '#B0B0B0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(8px)',
                    '&:hover': { color: '#fff', background: 'rgba(253,129,18,0.85)', boxShadow: '0 8px 20px rgba(253,129,18,0.4)' },
                  }}
                >
                  <LinkedInIcon fontSize="small" />
                </MotionLink>
                <MotionLink
                  href="https://www.instagram.com/slgt_digital_marketing_agency"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  whileHover={{ y: -4, scale: 1.08 }}
                  sx={{
                    color: '#B0B0B0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(8px)',
                    '&:hover': { color: '#fff', background: 'rgba(253,129,18,0.85)', boxShadow: '0 8px 20px rgba(253,129,18,0.4)' },
                  }}
                >
                  <InstagramIcon fontSize="small" />
                </MotionLink>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ color: '#ff9800', mb: 2 }}>Services</Typography>
              <nav>
                {featuredServices.map((service, index) => (
                  <MotionLink
                    key={index}
                    onClick={() => navigate(service.path)}
                    whileHover={{ x: 5 }}
                    aria-label={service.name}
                    sx={{
                      color: '#B0B0B0',
                      display: 'block',
                      mb: 1,
                      cursor: 'pointer',
                      textDecoration: 'none',
                      '&:hover': { color: '#ff9800' }
                    }}
                  >
                    {service.name}
                  </MotionLink>
                ))}
              </nav>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="h6" sx={{ color: '#ff9800', mb: 2 }}>Quick Links</Typography>
              <nav>
                {importantLinks.map((link, index) =>
                  link.isExternal ? (
                    <MotionLink
                      key={index}
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ x: 5 }}
                      sx={{
                        color: '#B0B0B0',
                        display: 'block',
                        mb: 1,
                        textDecoration: 'none',
                        '&:hover': { color: '#ff9800' }
                      }}
                    >
                      {link.name}
                    </MotionLink>
                  ) : (
                    <MotionLink
                      key={index}
                      onClick={() => navigate(link.path)}
                      whileHover={{ x: 5 }}
                      sx={{
                        color: '#B0B0B0',
                        display: 'block',
                        mb: 1,
                        cursor: 'pointer',
                        textDecoration: 'none',
                        '&:hover': { color: '#ff9800' }
                      }}
                    >
                      {link.name}
                    </MotionLink>
                  )
                )}
              </nav>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="h6" sx={{ color: '#ff9800', mb: 2 }}>Contact Us</Typography>
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start' }}>
                <LocationOnIcon sx={{ color: '#ff9800', mr: 1, mt: 0.5 }} />
                <Link href="https://g.co/kgs/Rp4yNxM" aria-label="map" sx={{ color: '#B0B0B0', textDecoration: 'none', '&:hover': { color: '#ff9800' } }}>
                  1st Floor, Gandhi Market, Anakapalle, Visakhapatnam, Andhra Pradesh - 531001
                </Link>
              </Box>
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                <PhoneIcon sx={{ color: '#ff9800', mr: 1 }} />
                <Link href="tel:+918500501823" aria-label="Phone" sx={{ color: '#B0B0B0', textDecoration: 'none', '&:hover': { color: '#ff9800' } }}>
                  +91 8500501823
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ color: '#ff9800', mr: 1 }} />
                <Link href="mailto:slgtmarketingagency@gmail.com" aria-label="Email" sx={{ color: '#B0B0B0', textDecoration: 'none', '&:hover': { color: '#ff9800' } }}>
                  slgtmarketingagency@gmail.com
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center'}}>
                <InstagramIcon sx={{ color: '#ff9800', mr: 1 }} />
                <Link href="https://www.instagram.com/slgt_digital_marketing_agency" target="_blank" rel="noopener noreferrer" aria-label="Instagram" sx={{ color: '#B0B0B0', textDecoration: 'none', '&:hover': { color: '#ff9800' } }}>
                  Follow us on Instagram
                </Link>
              </Box>
            </Grid>
          </Grid>
 
          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', sm: 'flex-start' },
            gap: 2
          }}>
            <Typography variant="body2" sx={{ color: '#B0B0B0', fontSize: '0.85rem' }}>
              © 2024 SLGT Marketing Agency. All Rights Reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Link onClick={() => navigate('/privacy-policy')} sx={{ color: '#B0B0B0', cursor: 'pointer', '&:hover': { color: '#ff9800' } }}>
                Privacy Policy
              </Link>
              <Link
                href="https://g.co/kgs/Rp4yNxM"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View SLGT Digital on Google Maps"
                sx={{ color: '#B0B0B0', cursor: 'pointer', '&:hover': { color: '#ff9800' } }}
              >
                📍Find Us on Google Maps
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    </footer>
  );
}
